
import api from "./Api";
import {ICart, ICartRef, ICartSharedContactResponse, IBundleSelectionItem, IQuoteRequest, IShareCartRequest, IUserMatchesResponse} from "./Models/Cart";
import {ICartUpdateResponse, ICheckoutQuestionsResponse, IStartCheckoutResponse} from "./Models/Order";
import {IActionResponse} from "./Models/ActionResponse";
import {ICollectionResponse} from "./Models/Wrappers";

const preparedCartApi = api.injectEndpoints({
    endpoints: (build) => ({
        getPreparedCart: build.query<ICart, { cartId: string}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}`,
                method: "GET"
            }),
            // Tagged so checkout can force a refetch after it re-conforms drifted bundles server-side.
            providesTags: () => [{ type: 'cart' as const, id: 'PREPARED' }],
        }),
        getPreparedCartRefs: build.query<ICollectionResponse<ICartRef>, void>({
            query: (arg) => ({
                url: `/preparedCart/refs`,
                method: "GET"
            }),
        }),
        startPreparedCartCheckout: build.mutation<IStartCheckoutResponse, { cartId: string }>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/checkout`,
                method: 'POST',
            }),
            // When checkout re-conforms drifted bundles server-side, refetch the prepared cart so the
            // updated bundle line items are shown before the shopper checks out again.
            invalidatesTags: (result) => result?.bundleChanged ? [{ type: 'cart' as const, id: 'PREPARED' }] : [],
        }),
        sharePreparedCart: build.mutation<IActionResponse, { cartId: string, request: IShareCartRequest }>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/share`,
                method: 'POST',
                body: arg.request
            }),
        }),
        shareCart: build.mutation<IActionResponse, { request: IShareCartRequest }>({
            query: (arg) => ({
                url: `/cart/share`,
                method: 'POST',
                body: arg.request
            }),
        }),
        getQuoteRequestQuestions: build.query<ICheckoutQuestionsResponse, void>({
            query: () => ({ url: `/cart/quoteRequestQuestions` }),
        }),
        requestQuote: build.mutation<IActionResponse, { request: IQuoteRequest }>({
            query: (arg) => ({
                url: `/cart/quoteRequest`,
                method: 'POST',
                body: arg.request
            }),
        }),
        customerComment: build.mutation<IActionResponse, { cartId: string, comment: string }>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/comment`,
                method: 'POST',
                body: {comment: arg.comment}
            }),
        }),
        saveCart: build.mutation<IActionResponse, { request: IQuoteRequest }>({
            query: (arg) => ({
                url: `/cart/convertToPreparedCart`,
                method: 'POST',
                body: arg.request
            }),
        }),
        getPossibleUserMatches: build.query<IUserMatchesResponse, void>({
            query: () => ({
                url: `/preparedCart/contactMatches`,
                method: 'GET'
            }),
        }),
        getExistingUserMatch: build.query<IUserMatchesResponse, {preparedCartId: string}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.preparedCartId}/contactMatch`,
                method: 'GET'
            }),
        }),
        getShareableContacts: build.query<ICartSharedContactResponse, { companyId: string } >({
            query: (arg) => ({
                url: `/preparedCart/shareableContacts/${arg.companyId}`,
                method: 'GET'
            }),
        }),
        updatePreparedCartProduct: build.mutation<IActionResponse, {cartId: string, wsItemId: string, quantity: number}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/items/${arg.wsItemId}`,
                method: 'PUT',
                body: { quantity: arg.quantity }
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    preparedCartApi.util.updateQueryData('getPreparedCart', {cartId: arg.cartId}, (draft) => {
                        if(!result.data?.success) return;
                        const item = draft.products.find(p => p.workspaceItemId === arg.wsItemId);
                        const bundleItem = draft.bundles.find(b => b.workspaceItemId === arg.wsItemId);
                        if(arg.quantity === 0){
                            if(item) {
                                draft.removedProducts.push(item.productId);
                                draft.products = draft.products.filter(p => p.workspaceItemId !== arg.wsItemId);
                            }

                            if(bundleItem) {
                                draft.removedBundles.push(bundleItem.bundleId);
                                draft.bundles = draft.bundles.filter(b => b.workspaceItemId !== arg.wsItemId);
                            }
                            return;
                        }
                        
                        if (item) {
                            item.quantity = arg.quantity;
                        }
                        if (bundleItem) {
                            bundleItem.quantity = arg.quantity;
                        }
                    })
                );
            }
        }),
        addToPreparedCart: build.mutation<ICartUpdateResponse, {cartId: string, catalog: string, productId: string, quantity?: number}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/addItem/${arg.catalog}/${arg.productId}?quantity=${arg.quantity || 1}`,
                method: 'POST',
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    preparedCartApi.util.updateQueryData('getPreparedCart', {cartId: arg.cartId}, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        addBundleToPreparedCart: build.mutation<ICartUpdateResponse, {cartId: string, bundleId: string, quantity?: number, selection?: IBundleSelectionItem[]}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/addBundle/${arg.bundleId}`,
                method: 'POST',
                body: { quantity: arg.quantity ?? 1, selection: arg.selection ?? [] },
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    preparedCartApi.util.updateQueryData('getPreparedCart', {cartId: arg.cartId}, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
        // Update the selection (and optionally quantity) of a bundle already on a prepared cart.
        // The server enforces AllowBundleConfiguration / CanAddAdditionalItems and validates that
        // rep-locked selections are preserved.
        updatePreparedCartBundle: build.mutation<ICartUpdateResponse, {cartId: string, wsItemId: string, quantity: number, selection: IBundleSelectionItem[]}>({
            query: (arg) => ({
                url: `/preparedCart/${arg.cartId}/bundles/${arg.wsItemId}`,
                method: 'PUT',
                body: { quantity: arg.quantity, selection: arg.selection },
            }),
            async onQueryStarted(arg, queryApi) {
                const result = await queryApi.queryFulfilled;
                queryApi.dispatch(
                    preparedCartApi.util.updateQueryData('getPreparedCart', {cartId: arg.cartId}, (draft) => {
                        if (result?.data?.success && result?.data?.cart) {
                            Object.assign(draft, result.data.cart);
                        }
                    })
                );
            }
        }),
    }),
});

export const {
    useGetQuoteRequestQuestionsQuery,
    useGetPreparedCartQuery,
    useGetPreparedCartRefsQuery,
    useStartPreparedCartCheckoutMutation,
    useUpdatePreparedCartProductMutation,
    useAddToPreparedCartMutation,
    useAddBundleToPreparedCartMutation,
    useUpdatePreparedCartBundleMutation,
    useRequestQuoteMutation,
    useSharePreparedCartMutation,
    useShareCartMutation,
    useGetShareableContactsQuery,
    useLazyGetShareableContactsQuery,
    useGetPossibleUserMatchesQuery,
    useLazyGetPossibleUserMatchesQuery,
    useLazyGetExistingUserMatchQuery,
    useSaveCartMutation,
    useCustomerCommentMutation

} = preparedCartApi;